class SoundService {
  private goodTileSound: HTMLAudioElement | null = null;
  private deathTileSound: HTMLAudioElement | null = null;
  private dangerTileSound: HTMLAudioElement | null = null;
  private phoenixSound: HTMLAudioElement | null = null; // For phoenix revival effect. Lazy load
  private isEnabled: boolean = true;
  private readonly AUDIO_ENABLED_KEY = 'deathx-fun-audio-enabled';

  constructor() {
    this.loadAudioPreference();
    this.initializeSounds();
  }

  private loadAudioPreference() {
    try {
      const storedPreference = localStorage.getItem(this.AUDIO_ENABLED_KEY);
      if (storedPreference !== null) {
        this.isEnabled = JSON.parse(storedPreference);
      }
    } catch (error) {
      console.warn('Failed to load audio preference from localStorage:', error);
      // Default to enabled if there's an error
      this.isEnabled = true;
    }
  }

  private saveAudioPreference() {
    try {
      localStorage.setItem(this.AUDIO_ENABLED_KEY, JSON.stringify(this.isEnabled));
    } catch (error) {
      console.warn('Failed to save audio preference to localStorage:', error);
    }
  }

  private initializeSounds() {
    try {
      // Initialize good tile sound
      this.goodTileSound = new Audio("/audio/good-tile.mp3");
      this.goodTileSound.preload = "auto";
      this.goodTileSound.volume = 0.7;

      // Initialize death tile sound
      this.deathTileSound = new Audio("/audio/death-tile.mp3");
      this.deathTileSound.preload = "auto";
      this.deathTileSound.volume = 0.8;

      // Initialize danger tile sound
      this.dangerTileSound = new Audio("/audio/danger-tile.mp3");
      this.dangerTileSound.preload = "auto";
      this.dangerTileSound.volume = 0.7;

      // Handle loading errors gracefully
      this.goodTileSound.addEventListener("error", () => {
        console.warn("Failed to load good tile sound");
        this.goodTileSound = null;
      });

      this.deathTileSound.addEventListener("error", () => {
        console.warn("Failed to load death tile sound");
        this.deathTileSound = null;
      });

      this.dangerTileSound.addEventListener("error", () => {
        console.warn("Failed to load danger tile sound");
        this.dangerTileSound = null;
      });
    } catch (error) {
      console.warn("Failed to initialize sounds:", error);
    }
  }

  public loadPhoenixSound() {
    if (this.phoenixSound) return;

    this.phoenixSound = new Audio("/audio/phoenix.mp3");
    this.phoenixSound.preload = "auto";
    this.phoenixSound.volume = 0.8;

    this.phoenixSound.addEventListener("error", () => {
      console.warn("Failed to load phoenix sound");
      this.phoenixSound = null;
    });
  }

  public playGoodTile() {
    if (!this.isEnabled || !this.goodTileSound) return;

    try {
      // Reset to beginning and play
      this.goodTileSound.currentTime = 0;
      this.goodTileSound.play().catch(() => {
        // Ignore play failures (user interaction required, etc.)
      });
    } catch (error) {
      console.warn("Failed to play good tile sound:", error);
    }
  }

  public playDeathTile() {
    if (!this.isEnabled || !this.deathTileSound) return;

    try {
      // Reset to beginning and play
      this.deathTileSound.currentTime = 0;
      this.deathTileSound.play().catch(() => {
        // Ignore play failures (user interaction required, etc.)
      });
    } catch (error) {
      console.warn("Failed to play death tile sound:", error);
    }
  }

  public playDangerTile() {
    if (!this.isEnabled || !this.dangerTileSound) return;

    try {
      // Reset to beginning and play
      this.dangerTileSound.currentTime = 0;
      this.dangerTileSound.play().catch(() => {
        // Ignore play failures (user interaction required, etc.)
      });
    } catch (error) {
      console.warn("Failed to play danger tile sound:", error);
    }
  }

  public playPhoenixSound() {
    if (!this.isEnabled || !this.phoenixSound) return;

    try {
      // Reset to beginning and play
      this.phoenixSound.currentTime = 0;
      this.phoenixSound.play().catch(() => {
        // Ignore play failures (user interaction required, etc.)
      });
    } catch (error) {
      console.warn("Failed to play phoenix sound:", error);
    }
  }

  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    this.saveAudioPreference();
  }

  public isAudioEnabled(): boolean {
    return this.isEnabled;
  }

}

// Export singleton instance
export const soundService = new SoundService();
